<template>
  <div :class="[useAddOns ? 'container-fluid' : 'container', { bo_compact: compactMode }]">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div style="max-width:1860px; margin:0 auto">
          <!-- The search row mirrors the column grid below, so its content aligns with the product column -->
          <div class="row mt-3">
            <div :class="categoriesColClass"></div>
            <div :class="productsColClass">
              <!-- flex-wrap so a narrow screen drops the toggle to its own line; without it the row
                   just squeezed the button until its label broke in two. -->
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div class="position-relative flex-grow-1" style="max-width:450px">
                  <input class="form-control" type="text" v-model="searchQuery"
                         placeholder="Søk etter produkt, valg eller tillegg..." style="padding-right:32px"/>
                  <a v-if="searchQuery" class="bo_search-clear" title="Tøm søket" @click="searchQuery = ''">
                    <font-awesome-icon :icon="['fas', 'times']"/>
                  </a>
                </div>
                <select v-if="locations.length > 1" class="form-select" v-model="locationFilter"
                        style="max-width:230px"
                        title="Vis kun produkter som er tilgjengelige på én avdeling">
                  <option value="">Alle avdelinger</option>
                  <option v-for="location in locations" :key="location.id" :value="location.id">
                    {{ location.name }}
                  </option>
                </select>
                <!-- The edit forms' words («Tilgjengelig på følgende kanaler»), so the filter reads as the same choice. -->
                <select v-if="usesChannels" class="form-select" v-model="channelFilter"
                        style="max-width:260px"
                        title="Vis kun produkter som selges på én kanal">
                  <option value="">Alle kanaler</option>
                  <option v-for="channel in channels" :key="channel.id" :value="channel.id">
                    {{ channel.name }}
                  </option>
                </select>
                <button type="button" class="bo_btn_secondary flex-shrink-0"
                        :title="compactMode ? 'Vis bilder og beskrivelser igjen' : 'Kompakt visning: skjuler bilder og beskrivelser'"
                        @click="compactMode = !compactMode">
                  <font-awesome-icon :icon="['fas', compactMode ? 'image' : 'list']"/>
                  {{ compactMode ? 'Full visning' : 'Kompakt visning' }}
                </button>
              </div>
            </div>
            <div v-if="useAddOns" class="col-12 bo_col-addons"></div>
          </div>
          <div class="row mt-4">
            <div :class="categoriesColClass">
              <Categories :categories="visibleCategories" :itemCounts="itemCountsByCategoryId" :activeCategoryId="activeCategoryId" @openPanel="openSidePanel" @refresh="refreshProducts"/>
            </div>
            <div :class="productsColClass">
                <div v-if="(searchQuery.trim() || locationFilter || channelFilter) && filteredItemsOfCategories.length === 0" class="mt-4 text-muted">
                  <span v-if="searchQuery.trim()">Ingen produkter samsvarer med "{{ searchQuery }}"{{ filterPlace }}.</span>
                  <span v-else>Ingen produkter er tilgjengelige{{ filterPlace }}.</span>
                </div>
                <div v-for="itemsOfCategory in filteredItemsOfCategories" :key="getCategoryElementId(itemsOfCategory)" class="bo_items-of-category" :id="getCategoryElementId(itemsOfCategory)">
                  <div class="bo_items_of_category_panel d-flex align-items-center flex-wrap">
                    <span class="bo_items-of-category-header mt-0"> {{ itemsOfCategory.name }} </span>
                    <span v-if="!isEmployee" class="ms-auto text-nowrap">
                      <!-- See Categories.vue: the bare glyph did not read as something you could press. -->
                      <button type="button" class="bo_btn_secondary bo_btn_compact me-2" @click="goToSortItemsPage(itemsOfCategory.id)">
                        <img src="@/assets/images/sort_order.png" width="18" class="bo_btn_icon" alt="" />Rekkefølge
                      </button>
                      <button type="button" class="bo_btn" @click="openSidePanel('itemCreate', { categoryId: itemsOfCategory.id })"> + Legg til produkt </button>
                    </span>
                  </div>
                  <ItemsOfCategory :items="itemsOfCategory.items" :vat="vat" :soldOut="soldOut" :locations="locations"
                                   :categoryChannelIds="itemsOfCategory.availableForChannelIds" @openPanel="openSidePanel" @refresh="refreshProducts"/>
                </div>
            </div>
            <div v-if="useAddOns" class="col-12 bo_col-addons">
              <AddOnGroups :addOnGroups="filteredAddOnGroups" :addOns="addOns" :vat="vat" :searchActive="searchQuery.trim().length > 0" :soldOut="soldOut" @openPanel="openSidePanel" @refresh="refreshProducts"/>
            </div>
          </div>
        </div>

      <!-- Side Panels -->
      <SidePanel :modelValue="showPanel" @update:modelValue="onPanelToggle" :width="panelWidth" :side="panelSide" :lockScroll="true"
                 :contentKey="sidePanelKey" :swapDirection="swapDirection"
                 :closeButtonTop="panelHeaderStuck ? '58px' : '15px'">
        <component
          v-if="sidePanelType"
          :is="panelComponent"
          :key="sidePanelKey"
          :panelMode="true"
          v-bind="sidePanelProps"
          @saved="onSidePanelSaved"
          @openPanel="openSidePanel"
          @refresh="refreshProducts"
          @templateDeleted="libraryNeedsRefresh = true"
          @headerStuck="panelHeaderStuck = $event"
        />
      </SidePanel>
  </div>
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import Categories from "@/components/product/Categories";
  import ProductService from "@/services/product/ProductService";
  import LocationService from "@/services/location/LocationService";
  import ItemsOfCategory from "@/components/product/ItemsOfCategory";
  import AddOnGroups from "@/components/product/AddOnGroups";
  import SidePanel from "@/components/other/SidePanel";
  import CategoryCreate from "@/pages/products/CategoryCreate";
  import CategoryEdit from "@/pages/products/CategoryEdit";
  import ItemCreate from "@/pages/products/ItemCreate";
  import ItemEdit from "@/pages/products/ItemEdit";
  import AddOnGroupCreate from "@/pages/products/AddOnGroupCreate";
  import AddOnGroupEdit from "@/pages/products/AddOnGroupEdit";
  import AddOnCreate from "@/pages/products/AddOnCreate";
  import AddOnEdit from "@/pages/products/AddOnEdit";
  import AddOnTemplates from "@/pages/products/AddOnTemplates";
  import AddOnTemplateCreate from "@/pages/products/AddOnTemplateCreate";
  import AddOnTemplateEdit from "@/pages/products/AddOnTemplateEdit";
  import CategoriesSort from "@/pages/products/CategoriesSort";
  import ItemsOfCategorySort from "@/pages/products/ItemsOfCategorySort";
  import AddOnGroupsSort from "@/pages/products/AddOnGroupsSort";
  import AddOnsOfAddOnGroupSort from "@/pages/products/AddOnsOfAddOnGroupSort";
  import ChannelUtil from "@/util/ChannelUtil";
  import ItemUtil from "@/util/ItemUtil";
  import MerchantUtil from "@/util/MerchantUtil";
  import SoldOutUtil from "@/util/SoldOutUtil";
  import UserUtil from "@/util/UserUtil";

  const PANEL_COMPONENTS = {
    categoryCreate: CategoryCreate,
    categoryEdit: CategoryEdit,
    itemCreate: ItemCreate,
    itemEdit: ItemEdit,
    addOnGroupCreate: AddOnGroupCreate,
    addOnGroupEdit: AddOnGroupEdit,
    addOnCreate: AddOnCreate,
    addOnEdit: AddOnEdit,
    addOnTemplates: AddOnTemplates,
    addOnTemplateCreate: AddOnTemplateCreate,
    addOnTemplateEdit: AddOnTemplateEdit,
    categoriesSort: CategoriesSort,
    itemsOfCategorySort: ItemsOfCategorySort,
    addOnGroupsSort: AddOnGroupsSort,
    addOnsOfAddOnGroupSort: AddOnsOfAddOnGroupSort
  };

  const PANEL_WIDTHS = {
    categoryCreate: '650px',
    categoryEdit: '650px',
    itemCreate: '800px',
    itemEdit: '800px',
    addOnGroupCreate: '650px',
    addOnGroupEdit: '650px',
    addOnCreate: '650px',
    addOnEdit: '650px',
    addOnTemplates: '650px',
    addOnTemplateCreate: '650px',
    addOnTemplateEdit: '650px',
    categoriesSort: '650px',
    itemsOfCategorySort: '650px',
    addOnGroupsSort: '650px',
    addOnsOfAddOnGroupSort: '650px'
  };

  export default {
    components: {LoadingSpinner, Categories, ItemsOfCategory, AddOnGroups, SidePanel,
      CategoryCreate, CategoryEdit, ItemCreate, ItemEdit,
      AddOnGroupCreate, AddOnGroupEdit, AddOnCreate, AddOnEdit,
      AddOnTemplates, AddOnTemplateCreate, AddOnTemplateEdit,
      CategoriesSort, ItemsOfCategorySort, AddOnGroupsSort, AddOnsOfAddOnGroupSort},
    data() {
      return {
        loading: true,

        searchQuery: "",
        locationFilter: "", //'' = every location; otherwise a location id the item list is narrowed to
        channelFilter: "", //'' = every channel; otherwise a channel id (ChannelUtil) the item list is narrowed to
        locations: [],
        compactMode: localStorage.getItem('bo_products_compact') === 'true',
        activeCategoryId: '',
        scrollTicking: false,

        categories: [],
        items: [], //Pristine copy, getItemsOfCategories overwrites price fields for display
        itemsOfCategories: [],
        addOnGroups: [],
        addOns: [],
        soldOutItems: [],
        vat: {},

        // Side panel state
        showPanel: false,
        sidePanelType: null,
        sidePanelParams: {},
        sidePanelKey: 0,
        swapDirection: 'forward', //forward: new panel slides in over the current one, back: current panel slides out
        panelHeaderStuck: false, //True when the open panel's sticky header is stuck to the top
        libraryScrollTop: 0, //Scroll position of the library list, restored when coming back from a library entry
        libraryNeedsRefresh: false //An add-on was deleted in the library -> refresh products when it closes so it disappears from the groups
      };
    },
    computed: {
      useAddOns(){
        return MerchantUtil.useAddOns();
      },
      categoriesColClass(){
        return this.useAddOns ? 'col-lg-3' : 'col-lg-5';
      },
      productsColClass(){
        //bo_col gives the plain col the same vertical padding as the [class*="col-"] rule
        return this.useAddOns ? 'col bo_col' : 'col-lg-7';
      },
      /**
       * Where everything is sold out, per location. A mark applies only to the location it was made for,
       * so with one location chosen only that location's marks count, and with «Alle avdelinger» the
       * badge names the locations (see SoldOutUtil).
       */
      soldOut(){
        return SoldOutUtil.index(this.soldOutItems, this.locations, this.locationFilter);
      },
      /**
       * The category list mirrors what the product column actually renders, so clicking one always lands
       * somewhere and the counts describe what is on screen. Both the search box and the location filter
       * therefore narrow it.
       *
       * Deliberately the ORIGINAL category objects, filtered by id — not the rebuilt ones from
       * filteredItemsOfCategories. getItemsOfCategories keeps only {id, sortOrder, name, items,
       * availableAtLocationIds}, while deleting a category posts category.merchant and category.imageUrl;
       * handing those objects over would silently orphan the category's image in S3.
       */
      visibleCategories(){
        let visibleIds = this.filteredItemsOfCategories.map(itemsOfCategory => itemsOfCategory.id);
        return this.categories.filter(category => visibleIds.indexOf(category.id) > -1);
      },
      itemCountsByCategoryId(){
        let counts = {};
        for(let i = 0; i < this.filteredItemsOfCategories.length; i++){
          counts[this.filteredItemsOfCategories[i].id] = this.filteredItemsOfCategories[i].items.length;
        }
        return counts;
      },
      locationFilterName(){
        let location = this.locations.find(location => location.id === this.locationFilter);
        return location ? location.name : '';
      },
      usesChannels(){
        return ChannelUtil.merchantUsesChannels();
      },
      channels(){
        return ChannelUtil.CHANNELS;
      },
      /** « på Grefsen i kassen» — what the location and channel filters narrowed to, for the empty-list message. */
      filterPlace(){
        let channel = ChannelUtil.channel(this.channelFilter);
        return (this.locationFilter ? ' på ' + this.locationFilterName : '') + (channel ? ' ' + channel.where : '');
      },
      filteredItemsOfCategories(){
        let query = this.searchQuery.trim().toLowerCase();
        let source = this.itemsOfCategoriesForChannel;
        if(!query){
          return source;
        }
        let filtered = [];
        for(let i = 0; i < source.length; i++){
          let itemsOfCategory = source[i];
          if(itemsOfCategory.name.toLowerCase().includes(query)){
            filtered.push(itemsOfCategory); //Category name matches, show all its items
            continue;
          }
          let matchingItems = itemsOfCategory.items.filter(item =>
              (item.name && item.name.toLowerCase().includes(query))
              || (item.menuNr && String(item.menuNr).toLowerCase().includes(query)));
          if(matchingItems.length > 0){
            filtered.push(Object.assign({}, itemsOfCategory, { items: matchingItems }));
          }
        }
        return filtered;
      },
      /**
       * The categories narrowed to one location. A category is dropped entirely when it is not available
       * there — its items cannot be either, since a category's own availableAtLocationIds is what limits
       * which locations its items may be offered at (see ItemEdit).
       */
      itemsOfCategoriesForLocation(){
        if(!this.locationFilter){
          return this.itemsOfCategories;
        }
        let filtered = [];
        for(let i = 0; i < this.itemsOfCategories.length; i++){
          let itemsOfCategory = this.itemsOfCategories[i];
          let categoryLocationIds = itemsOfCategory.availableAtLocationIds || [];
          if(categoryLocationIds.length > 0 && categoryLocationIds.indexOf(this.locationFilter) === -1){
            continue;
          }
          let matchingItems = itemsOfCategory.items.filter(item =>
              (item.availableAtLocationIds || []).indexOf(this.locationFilter) > -1);
          if(matchingItems.length > 0){
            filtered.push(Object.assign({}, itemsOfCategory, { items: matchingItems }));
          }
        }
        return filtered;
      },
      /**
       * Narrowed further to one channel: the products whose own channels, within their category's, include it
       * (ChannelUtil.channelIdsOf — what each product's channel line shows).
       */
      itemsOfCategoriesForChannel(){
        if(!this.channelFilter){
          return this.itemsOfCategoriesForLocation;
        }
        let filtered = [];
        for(let i = 0; i < this.itemsOfCategoriesForLocation.length; i++){
          let itemsOfCategory = this.itemsOfCategoriesForLocation[i];
          let matchingItems = itemsOfCategory.items.filter(item =>
              ChannelUtil.channelIdsOf(item, itemsOfCategory.availableForChannelIds).includes(this.channelFilter));
          if(matchingItems.length > 0){
            filtered.push(Object.assign({}, itemsOfCategory, { items: matchingItems }));
          }
        }
        return filtered;
      },
      filteredAddOnGroups(){
        let query = this.searchQuery.trim().toLowerCase();
        if(!query){
          return this.addOnGroups;
        }
        return this.addOnGroups.filter(addOnGroup =>
            addOnGroup.name.toLowerCase().includes(query)
            || this.addOns.some(addOn => addOn.addOnGroupId === addOnGroup.id
                && addOn.name && addOn.name.toLowerCase().includes(query)));
      },
      isEmployee(){
        return UserUtil.isEmployee();
      },
      panelComponent(){
        return PANEL_COMPONENTS[this.sidePanelType] || null;
      },
      panelWidth(){
        return PANEL_WIDTHS[this.sidePanelType] || '580px';
      },
      panelSide(){
        const leftPanels = ['categoryCreate', 'categoryEdit', 'categoriesSort'];
        const topPanels = ['itemCreate', 'itemEdit', 'itemsOfCategorySort'];
        if(leftPanels.includes(this.sidePanelType)) return 'left';
        if(topPanels.includes(this.sidePanelType)) return 'top';
        return 'right';
      },
      sidePanelProps(){
        const params = this.sidePanelParams;
        switch(this.sidePanelType){
          case 'categoryEdit':
            return { categoryIdProp: params.categoryId };
          case 'itemCreate':
            return { categoryIdProp: params.categoryId };
          case 'itemEdit':
            return { categoryIdProp: params.categoryId, itemIdProp: params.itemId };
          case 'addOnGroupEdit':
            return { addOnGroupIdProp: params.addOnGroupId };
          case 'addOnCreate':
            return { addOnGroupIdProp: params.addOnGroupId };
          case 'addOnEdit':
            return { addOnGroupIdProp: params.addOnGroupId, addOnIdProp: params.addOnId };
          case 'addOnTemplates':
            return { vat: this.vat, addOns: this.addOns, initialScrollTopProp: this.libraryScrollTop };
          case 'addOnTemplateCreate':
            return { vat: this.vat, itemsProp: this.items, categoriesProp: this.categories };
          case 'addOnTemplateEdit':
            return { vat: this.vat, addOnTemplateIdProp: params.addOnTemplateId, addOnTemplateProp: params.addOnTemplate,
              itemsProp: this.items, categoriesProp: this.categories, addOnsProp: this.addOns, addOnGroupsProp: this.addOnGroups };
          case 'itemsOfCategorySort':
            return { categoryIdProp: params.categoryId };
          case 'addOnsOfAddOnGroupSort':
            return { addOnGroupIdProp: params.addOnGroupId };
          default:
            return {};
        }
      }
    },
    mounted() {
      this.fetchProducts();
      this.fetchLocations();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    },
    beforeUnmount() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    },
    watch: {
      showPanel(newVal){
        if(!newVal){
          //Keep the content mounted during the slide-out animation (0.3s)
          setTimeout(() => {
            if(!this.showPanel){
              this.sidePanelType = null;
              this.sidePanelParams = {};
            }
          }, 300);
        }
      },
      compactMode(newVal){
        localStorage.setItem('bo_products_compact', newVal ? 'true' : 'false');
      }
    },
    methods: {
      /**
       * Loaded separately from getProducts, which returns no locations. Only the id -> name mapping is
       * needed, so a failure just leaves the location badges and the filter hidden rather than breaking
       * the page — products are what this screen is for.
       */
      fetchLocations(){
        LocationService.listLocations().then(
            (response) => {
              this.locations = response.data || [];
            },
            (error) => {
              console.log(error);
            }
        );
      },
      fetchProducts(){
        ProductService.getProducts().then(
            (response) => {
              this.categories = response.data.categories;
              let items = response.data.items;
              this.items = JSON.parse(JSON.stringify(items)); //Copy before getItemsOfCategories overwrites price fields for display
              this.vat = response.data.vat;
              this.itemsOfCategories = this.getItemsOfCategories(items, this.vat);
              this.addOnGroups = response.data.addOnGroups;
              this.addOns = response.data.addOns;
              this.soldOutItems = response.data.soldOutItems || [];

              this.loading = false;
              this.$nextTick(() => this.updateActiveCategory());
            },
            (error) => {
              console.log(error);
            }
        );
      },
      onScroll(){
        if(this.scrollTicking){
          return;
        }
        this.scrollTicking = true;
        window.requestAnimationFrame(() => {
          this.updateActiveCategory();
          this.scrollTicking = false;
        });
      },
      updateActiveCategory(){
        //Scroll-spy: the active category is the section crossing the top of the viewport
        let sections = document.getElementsByClassName('bo_items-of-category');
        let activeElementId = '';
        for(let i = 0; i < sections.length; i++){
          let rect = sections[i].getBoundingClientRect();
          if(rect.top <= 120 && rect.bottom > 120){
            activeElementId = sections[i].id;
            break;
          }
        }
        if(!activeElementId && sections.length > 0 && sections[0].getBoundingClientRect().top > 120){
          activeElementId = sections[0].id; //Above the first section
        }
        this.activeCategoryId = activeElementId ? activeElementId.replace('cat_', '') : '';
      },
      refreshProducts(){
        this.fetchProducts();
      },
      openSidePanel(type, params){
        if(this.sidePanelType === 'addOnTemplates' && ['addOnTemplateCreate', 'addOnTemplateEdit'].includes(type)){
          //Remember the library scroll position, restored when coming back from the entry
          let panel = document.querySelector('.side-panel');
          this.libraryScrollTop = panel ? panel.scrollTop : 0;
        } else if(type === 'addOnTemplates'){
          this.libraryScrollTop = 0; //Opened fresh from the products page
          this.libraryNeedsRefresh = false;
        }
        this.swapDirection = 'forward';
        this.panelHeaderStuck = false;
        this.sidePanelType = type;
        this.sidePanelParams = params || {};
        this.sidePanelKey++; // force re-mount of component
        this.showPanel = true;
      },
      goBackToLibrary(){
        //The library list stays beneath library entries, the entry slides out to reveal it
        this.swapDirection = 'back';
        this.panelHeaderStuck = false;
        this.sidePanelType = 'addOnTemplates';
        this.sidePanelParams = {};
        this.sidePanelKey++;
      },
      onPanelToggle(value){
        if(!value && ['addOnTemplateCreate', 'addOnTemplateEdit'].includes(this.sidePanelType)){
          this.goBackToLibrary(); //Closing a library entry brings the user back to the library list
          return;
        }
        if(!value && this.sidePanelType === 'addOnTemplates' && this.libraryNeedsRefresh){
          //An add-on was deleted in the library — refresh so it's gone from the groups too
          this.libraryNeedsRefresh = false;
          this.fetchProducts();
        }
        this.showPanel = value;
      },
      closeSidePanel(){
        this.showPanel = false;
      },
      onSidePanelSaved(){
        let cameFromLibrary = ['addOnTemplateCreate', 'addOnTemplateEdit'].includes(this.sidePanelType);
        this.fetchProducts();
        if(cameFromLibrary){
          this.goBackToLibrary();
        } else {
          this.closeSidePanel();
        }
      },
      getCategoryElementId(itemsOfCategory){
        let categoryElementId = 'cat_' + itemsOfCategory.id;
        return categoryElementId;
      },
      goToSortItemsPage(categoryId){
        this.openSidePanel('itemsOfCategorySort', { categoryId: categoryId });
      },
      goToAddItemPage(categoryId){
        this.openSidePanel('itemCreate', { categoryId: categoryId });
      },
      getItemsOfCategories(items, vat){
         var itemsOfCategories = [];
         for (var y = 0; y < items.length; y++) {
           //Update price to show
           items[y].price = ItemUtil.getItemSubselectionPriceRange(items[y]);
           items[y].priceTakeaway = ItemUtil.getItemSubselectionPriceRangeTakeaway(items[y], vat);
           items[y].priceDineInWithVat = ItemUtil.getItemSubselectionPriceRangeDineIn(items[y], vat);
         }
         for (var i = 0; i < this.categories.length; i++) {
           var category = this.categories[i];
           var tempItems = [];
           for (var x = 0; x < items.length; x++) {
             var item = items[x];
             if(item.categoryId == category.id){
               tempItems.push(item)
             }
           }
           //Remove already found items of category
           items = items.filter(function(item) {
             return tempItems.indexOf(item) === -1;
           });

           //Sort tempItems by asc sortOrder
           tempItems.sort(function(a, b) {
             if(a.sortOrder == b.sortOrder) { return 0; }
             if(a.sortOrder > b.sortOrder) {
               return 1;
             }else {
               return -1;
             }
           });

           var itemsOfCategory = {
             id  : category.id,
             sortOrder: category.sortOrder,
             name : category.name,
             //Carried through so the location filter can drop a whole category: a category's own
             //availableAtLocationIds is what limits where its items may be offered at all.
             availableAtLocationIds: category.availableAtLocationIds,
             //The category's channels bound its products' — the channel line and filter need them.
             availableForChannelIds: category.availableForChannelIds,
             items : tempItems
           };
           itemsOfCategories.push(itemsOfCategory);
         }

         //Sort itemsOfCategory by asc sortOrder
         itemsOfCategories.sort(function(a, b) {
           if(a.sortOrder == b.sortOrder) { return 0; }
           if(a.sortOrder > b.sortOrder) {
             return 1;
           }else {
             return -1;
           }
         });

          return itemsOfCategories;
       }
    }
  };
</script>

<style scoped>
.item-list{
  min-width: 450px;
}
</style>
